import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Printer, Sparkles, FileText, Presentation, QrCode, StickyNote, Megaphone, ListChecks, HelpCircle,
  ExternalLink, Cpu, Building2, Users, MapPin, Calculator, Signpost, Waves, DoorOpen, Tent, Tag,
} from "lucide-react";

// Standalone marketing enablement kit (internal, for the Ask Redline marketing/onboarding team).
// NOT part of the product app — no auth, no sidebar. Reachable directly at /kit.
// Three personas, each with its own messaging and asset set:
//   • board   — selling to a single community's volunteer board
//   • company — selling to a management firm that runs many communities
//   • rollout — resident-facing signage & collateral to launch the concierge on-site
const PERSONA_ASSETS = {
  board: [
    { id: "onepager", title: "Overview one-pager", desc: "The elevator pitch on a single sheet — benefits, how it works, and the concierge number.", icon: FileText },
    { id: "deck", title: "Board presentation", desc: "An 8-slide deck to walk a board through the problem, solution, privacy and rollout.", icon: Presentation },
    { id: "faq", title: "FAQ & objection handling", desc: "The eight questions boards always ask, with confident answers.", icon: HelpCircle },
    { id: "checklist", title: "Onboarding checklist", desc: "A 90-day playbook: pre-launch, launch, adopt and optimize.", icon: ListChecks },
    { id: "tech", title: "Technology & architecture", desc: "A one-page technical overview for IT-minded boards: the stack, security and scale.", icon: Cpu },
  ],
  company: [
    { id: "onepager", title: "Portfolio one-pager", desc: "The pitch to firm leadership — cost-to-serve, contract wins and standardized service across every community.", icon: FileText },
    { id: "deck", title: "Portfolio presentation", desc: "An 8-slide leadership deck making the business case: margin, retention and scale.", icon: Presentation },
    { id: "roi", title: "Portfolio ROI estimate", desc: "Enter door count and staff cost — get an estimate of calls deflected and dollars saved a year.", icon: Calculator },
    { id: "faq", title: "FAQ & objection handling", desc: "Procurement, security and portfolio objections — answered for a management company.", icon: HelpCircle },
    { id: "checklist", title: "Portfolio rollout checklist", desc: "A wave-by-wave playbook to roll out across many communities, from pilot to full portfolio.", icon: ListChecks },
    { id: "tech", title: "Technology & architecture", desc: "A one-page technical overview for IT & procurement: the stack, security and scale.", icon: Cpu },
  ],
  rollout: [
    { id: "poster", title: "QR opt-in poster", desc: "A bold, big-QR poster for mailrooms, clubhouses and high-traffic spots.", icon: QrCode },
    { id: "yardsign", title: "Yard / entrance sign", desc: "A full-color sign for entrances and common areas announcing the concierge is live.", icon: Signpost },
    { id: "amenitysign", title: "Amenity sign", desc: "For the pool, gym and clubhouse — \u201Cquestions about this amenity? Just text.\u201D", icon: Waves },
    { id: "windowsign", title: "Office window sign", desc: "For the management office door or window — \u201Coffice closed? We're still here.\u201D", icon: DoorOpen },
    { id: "tabletent", title: "Table tent", desc: "A fold-and-stand tent for clubhouse tables, front desks and community events.", icon: Tent },
    { id: "doorhanger", title: "Door hanger", desc: "Two per sheet — cut out and hang on doorknobs at launch or move-in.", icon: Tag },
    { id: "flyer", title: "Resident opt-in flyer", desc: "A full-page handout flyer with a scannable QR and text-to-join instructions.", icon: QrCode },
    { id: "card", title: "\u201CText us\u201D welcome card", desc: "Two cut-out welcome cards per sheet with QR — hand out at move-in.", icon: StickyNote },
    { id: "announcement", title: "Launch announcement", desc: "Ready-to-copy email + SMS blast for when the community goes live.", icon: Megaphone },
  ],
};

const PERSONAS = [
  { id: "board", label: "HOA board", icon: Users, hint: "A volunteer board for one community" },
  { id: "company", label: "Management company", icon: Building2, hint: "A firm running many communities" },
  { id: "rollout", label: "Community rollout", icon: MapPin, hint: "Signs & on-site materials for launch" },
];

const INTRO = {
  board: "Everything a rep needs to pitch and launch a single community: a board one-pager, an 8-slide board deck, an objection-handling sheet, a 90-day onboarding checklist and a technical overview.",
  company: "Everything a rep needs to win a management company: a leadership one-pager and portfolio deck, a live ROI estimate from their door count, an objection sheet for procurement and IT, a portfolio rollout checklist and a technical overview.",
  rollout: "Everything to launch the concierge on the ground: yard, amenity and office signs, table tents, door hangers, opt-in flyers and posters, welcome cards and a ready-to-send announcement — all pointing residents to opt in.",
};

const RED = "#D32F2F";

const Field = ({ label, hint, ...props }) => (
  <label className="block">
    <span className="block text-[11px] font-bold uppercase tracking-[0.14em] text-ink/50">{label}</span>
    <input
      {...props}
      className="mt-1.5 w-full rounded-xl border border-sand-300 bg-white px-3.5 py-2.5 text-sm text-ink outline-none transition-colors focus:border-redline-400 focus:ring-2 focus:ring-redline-500/20"
    />
    {hint && <span className="mt-1 block text-[11px] text-ink/40">{hint}</span>}
  </label>
);

export default function KitHub() {
  const [sp] = useSearchParams();
  const initialPersona = ["board", "company", "rollout"].includes(sp.get("persona")) ? sp.get("persona") : "board";
  const [persona, setPersona] = useState(initialPersona);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [link, setLink] = useState("");
  const [keyword, setKeyword] = useState("");
  const [voice, setVoice] = useState(true);
  const [communities, setCommunities] = useState("");
  const [units, setUnits] = useState("");
  const [rate, setRate] = useState("");

  const buildUrl = (asset) => {
    const p = new URLSearchParams({ asset, persona });
    if (name.trim()) p.set("name", name.trim());
    if (phone.trim()) p.set("phone", phone.trim());
    if (link.trim()) p.set("link", link.trim());
    if (keyword.trim()) p.set("keyword", keyword.trim());
    p.set("voice", voice ? "1" : "0");
    if (persona === "company") {
      if (communities.trim()) p.set("communities", communities.trim());
      if (units.trim()) p.set("units", units.trim());
      if (rate.trim()) p.set("rate", rate.trim());
    }
    return `/kit/print?${p.toString()}`;
  };

  const isCompany = persona === "company";
  const personaAssets = PERSONA_ASSETS[persona];

  const AssetCard = (a) => (
    <a
      key={a.id}
      href={buildUrl(a.id)}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`kit-asset-${a.id}`}
      className="group flex items-start gap-4 rounded-2xl border border-sand-200 bg-white p-5 transition-all hover:-translate-y-0.5 hover:border-redline-300 hover:shadow-md"
    >
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-colors group-hover:bg-redline-50" style={{ background: "#F3EFE9" }}>
        <a.icon className="h-5 w-5" style={{ color: RED }} strokeWidth={1.75} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-[15px] font-extrabold">{a.title}</span>
          <ExternalLink className="h-3.5 w-3.5 text-ink/30 opacity-0 transition-opacity group-hover:opacity-100" />
        </div>
        <p className="mt-1 text-[13px] leading-relaxed text-ink-600">{a.desc}</p>
        <span className="mt-2.5 inline-flex items-center gap-1.5 text-[13px] font-bold text-redline-600">
          <Printer className="h-3.5 w-3.5" /> Open &amp; print
        </span>
      </div>
    </a>
  );

  return (
    <div className="min-h-screen bg-sand-100 font-body text-ink">
      {/* Header */}
      <header className="border-b border-sand-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-6 py-5">
          <img src="/brand/redline-red.png" alt="Ask Redline" className="h-8" />
          <div className="ml-auto flex items-center gap-1.5 rounded-full bg-ink px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-white">
            <Sparkles className="h-3.5 w-3.5" style={{ color: "#F4A3A3" }} /> Marketing kit
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-10">
        <div className="max-w-2xl">
          <h1 className="text-4xl font-extrabold leading-[1.1]" style={{ fontFamily: "Poppins, sans-serif" }}>
            Ask Redline onboarding &amp; launch kit
          </h1>
          <p className="mt-3 text-[15px] leading-relaxed text-ink-600">{INTRO[persona]}</p>
        </div>

        {/* Persona switcher */}
        <section data-testid="kit-persona-switcher" className="mt-8">
          <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-ink/40">Who are you pitching?</div>
          <div className="mt-3 grid gap-2 sm:grid-cols-3">
            {PERSONAS.map((p) => {
              const active = persona === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`kit-persona-${p.id}`}
                  aria-pressed={active}
                  onClick={() => setPersona(p.id)}
                  className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-all ${active ? "border-ink bg-ink text-white shadow-sm" : "border-sand-200 bg-white text-ink/70 hover:border-ink/30"}`}
                >
                  <p.icon className="h-5 w-5 shrink-0" strokeWidth={1.75} style={active ? { color: "#F4A3A3" } : { color: RED }} />
                  <span className="min-w-0">
                    <span className="block text-[14px] font-extrabold leading-tight">{p.label}</span>
                    <span className={`block truncate text-[11px] ${active ? "text-sand-200/90" : "text-ink/40"}`}>{p.hint}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        {/* Personalization panel */}
        <section data-testid="kit-personalize" className="mt-8 rounded-3xl border border-sand-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-2 border-b border-sand-100 pb-3">
            <div className="text-[15px] font-extrabold">Personalize the kit</div>
            <span className="text-[12px] text-ink/40">Optional — leave blank to use generic placeholders</span>
          </div>
          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Field
              label={isCompany ? "Management company name" : "Community name"}
              placeholder={isCompany ? "Summit Community Management" : "Maplewood Community HOA"}
              hint={isCompany ? "Used on the leadership one-pager, deck, ROI and checklist." : undefined}
              value={name} onChange={(e) => setName(e.target.value)} data-testid="kit-input-name" />
            <Field label="Concierge phone number" placeholder="(555) 123-4567" value={phone}
              onChange={(e) => setPhone(e.target.value)} data-testid="kit-input-phone" />
            <Field label="Opt-in link (for QR codes)" placeholder="https://askredline.com/opt-in/maplewood"
              hint="The resident opt-in page — this is what every QR code will point to." value={link}
              onChange={(e) => setLink(e.target.value)} data-testid="kit-input-link" />
            <Field label="Opt-in keyword" placeholder="JOIN" value={keyword}
              onChange={(e) => setKeyword(e.target.value)} data-testid="kit-input-keyword" />
          </div>

          <label data-testid="kit-input-voice" className="mt-5 flex items-center gap-3 rounded-2xl border border-sand-200 bg-sand-50/60 px-4 py-3 cursor-pointer">
            <input type="checkbox" checked={voice} onChange={(e) => setVoice(e.target.checked)} className="h-4 w-4 accent-redline-500" />
            <span className="text-[13px] text-ink-700"><span className="font-bold">Residents can also call this number</span> — the voice concierge is on. Adds &ldquo;or call&rdquo; to every printed piece. Turn off for text-only communities.</span>
          </label>

          {isCompany && (
            <div data-testid="kit-roi-inputs" className="mt-6 border-t border-sand-100 pt-5">
              <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-ink/50">Portfolio ROI inputs</div>
              <p className="mt-1 text-[12px] text-ink/40">Feeds the Portfolio ROI estimate. Sensible defaults are used if left blank.</p>
              <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-3">
                <Field label="Communities" type="number" min="1" placeholder="10" value={communities}
                  onChange={(e) => setCommunities(e.target.value)} data-testid="kit-input-communities" />
                <Field label="Total units / doors" type="number" min="1" placeholder="2500" value={units}
                  onChange={(e) => setUnits(e.target.value)} data-testid="kit-input-units" />
                <Field label="Blended staff cost / hour ($)" type="number" min="1" placeholder="45" value={rate}
                  onChange={(e) => setRate(e.target.value)} data-testid="kit-input-rate" />
              </div>
            </div>
          )}
        </section>

        {/* Persona assets */}
        <section className="mt-10" data-testid="kit-section-persona">
          <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-ink/40">
            {persona === "company" ? "For the management company" : persona === "rollout" ? "Signs & materials for the community" : "For the HOA board"}
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {personaAssets.map(AssetCard)}
          </div>
        </section>

        <p className="mt-10 text-center text-[12px] text-ink/40">Internal marketing kit &middot; Ask Redline</p>
      </main>
    </div>
  );
}
